import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { TextAreaContainer } from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
  ModalSheetHeader,
  LeftSidePanel,
  LeftSidePanelHeading,
  LeftSidePanelTitle,
  LeftSidePanelDescription,
} from 'components/ModalSheet'
import JobViewModel from '../JobViewModel'
import { match } from 'utils/pattern-match-util'
import styles from './RequestScopeChangesDialog.sass'
import RequestScopeChangesViewModel, {
  RequestScopeChangesState,
} from './RequestScopeChangesViewModel'
import SearchField from 'components/SearchField'
import { useActivation } from 'hooks/mobx-hooks'
import ScopeChangeInventoryItem from './ListItems/ScopeChangeInventoryItem'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import ScopeChangeCustomItem from './ListItems/ScopeChangeCustomItem'
import OldRequestScopeChangesDialog from './OldRequestScopeChangesDialog'

interface RequestScopeChangesDialogProps {
  vm: JobViewModel
}

/**
 * The RequestScopeChangesDialog
 */
export default observer(function RequestScopeChangesDialog({
  vm,
}: RequestScopeChangesDialogProps) {
  const requestScopeChangesVm = vm.requestScopeChangesVM
  const disabled = requestScopeChangesVm.request.pending
  useActivation(requestScopeChangesVm)

  const modalContentRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0
    }
  }, [requestScopeChangesVm.state])

  if (!vm.job.inventoryItemPricingUsed) {
    return <OldRequestScopeChangesDialog vm={vm} />
  }

  const isCurrentStateSuccessfulOrFailed =
    requestScopeChangesVm.state === RequestScopeChangesState.Success ||
    requestScopeChangesVm.state === RequestScopeChangesState.Failed

  const canShowAddCustomItemEmptyState =
    requestScopeChangesVm.isServiceItemsEmpty &&
    requestScopeChangesVm.scopeChangeCustomItems.length === 0 &&
    requestScopeChangesVm.searchText.value !== '' &&
    requestScopeChangesVm.inventoryScrollProvider.isDone

  return (
    <ModalSheet
      showing={requestScopeChangesVm.showing}
      onRequestClose={requestScopeChangesVm.close}
      disableCloseOnOutsideClick={disabled}
      disableCloseOnEsc={disabled}
      leftSidePanelContent={
        isCurrentStateSuccessfulOrFailed ? undefined : (
          <RequestScopeChangesDialogLeftSidePanelContent
            requestScopeChangesState={requestScopeChangesVm.state}
          />
        )
      }
    >
      {match(requestScopeChangesVm.state, {
        [RequestScopeChangesState.RequestingChanges]: () => (
          <>
            <RequestScopeChangeHeading title="Select all items to update">
              <div className={styles.searchContainer}>
                <SearchField
                  autoFocus
                  stretch
                  className={styles.scopeSearch}
                  placeholder="Type to search"
                  onChange={requestScopeChangesVm.searchText.change}
                  value={requestScopeChangesVm.searchText.value}
                  showClearButton
                />
              </div>
            </RequestScopeChangeHeading>

            <GreyContainer centerContent>
              <ModalSheetContent ref={modalContentRef}>
                {canShowAddCustomItemEmptyState ? (
                  <div className={styles.addCustomItemContainerEmpty}>
                    <div className={styles.customItemTextEmpty}>
                      There are no service items with that name.
                    </div>
                    <Button
                      type="text"
                      onClick={requestScopeChangesVm.addScopeChange}
                      dataTest="request-scope-changes-add-button"
                    >
                      Add custom service
                    </Button>
                  </div>
                ) : (
                  <div className={styles.itemsContainer}>
                    {requestScopeChangesVm.serviceItems.map((item, index) => {
                      return (
                        <ScopeChangeInventoryItem
                          key={item.versionId}
                          item={item}
                          dataTestId={index}
                        />
                      )
                    })}
                    {requestScopeChangesVm.scopeChangeCustomItems.map(
                      (item, index) => {
                        return (
                          <ScopeChangeCustomItem
                            key={item.key}
                            item={item}
                            remove={requestScopeChangesVm.removeScopeChange}
                            dataTestId={index}
                          />
                        )
                      }
                    )}
                    <InfiniteScrollLoader
                      compact
                      scrollProvider={
                        requestScopeChangesVm.inventoryScrollProvider
                      }
                    />
                    <div className={styles.addCustomItemContainer}>
                      <div className={styles.customItemText}>
                        Not what you’re looking for?
                      </div>
                      <Button
                        type="text"
                        onClick={requestScopeChangesVm.addScopeChange}
                        dataTest="request-scope-changes-add-button"
                      >
                        Add custom service
                      </Button>
                    </div>
                  </div>
                )}
              </ModalSheetContent>
            </GreyContainer>

            <ModalSheetFooter>
              <FooterInventoryItemsQuantityInfo
                totalSelectedInventoryItemsCount={
                  requestScopeChangesVm.totalSelectedInventoryItemsCount
                }
              />
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  type="primary"
                  onClick={requestScopeChangesVm.moveToQuantityStep}
                  disabled={!requestScopeChangesVm.itemsSelected}
                  dataTest="request-scope-changes-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.UpdatingQuantity]: () => (
          <>
            <RequestScopeChangeHeading title="Compare and adjust quantities">
              <ItemHeadings vm={requestScopeChangesVm} />
            </RequestScopeChangeHeading>

            <GreyContainer>
              <ModalSheetContent ref={modalContentRef}>
                <div className={styles.itemsContainer}>
                  {requestScopeChangesVm.selectedInventoryItems.map(
                    (item, index) => {
                      return (
                        <ScopeChangeInventoryItem
                          updateQuantity
                          key={item.versionId}
                          item={item}
                          dataTestId={index}
                        />
                      )
                    }
                  )}
                  {requestScopeChangesVm.scopeChangeCustomItems.map(
                    (item, index) => {
                      return (
                        <ScopeChangeCustomItem
                          updateQuantity
                          key={item.key}
                          item={item}
                          remove={requestScopeChangesVm.removeScopeChange}
                          dataTestId={index}
                        />
                      )
                    }
                  )}
                </div>
              </ModalSheetContent>
            </GreyContainer>

            <ModalSheetFooter>
              <FooterInventoryItemsQuantityInfo
                totalSelectedInventoryItemsCount={
                  requestScopeChangesVm.totalSelectedInventoryItemsCount
                }
              />
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    requestScopeChangesVm.setState(
                      RequestScopeChangesState.RequestingChanges
                    )
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  onClick={requestScopeChangesVm.moveToSubmissionStep}
                  disabled={!requestScopeChangesVm.quantitiesChanged}
                  dataTest="request-scope-changes-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.ReviewingChanges]: () => (
          <>
            <RequestScopeChangeHeading title="Review and submit changes">
              <ItemHeadings vm={requestScopeChangesVm} />
            </RequestScopeChangeHeading>

            <div className={styles.flexContainer}>
              <ModalSheetContent ref={modalContentRef} expandable>
                {requestScopeChangesVm.relevantSelectedInventoryItems.map(
                  (item) => (
                    <div className={styles.listItem} key={item.versionId}>
                      <div className={styles.itemContainer}>
                        <span>
                          <strong>{item.name}</strong>
                        </span>
                        <div className={styles.itemDescription}>
                          {item.description}
                        </div>
                      </div>

                      <span>
                        <strong>{item.newQuantity}</strong>
                      </span>
                    </div>
                  )
                )}
                {requestScopeChangesVm.scopeChangeCustomItems.map((item) => (
                  <div className={styles.listItem} key={item.key}>
                    <span>
                      <strong>{item.name}</strong>
                    </span>
                    <span>
                      <strong>{item.quantity}</strong>
                    </span>
                  </div>
                ))}
                <div className={styles.listItemSeparator}></div>
                <div className={styles.filler}></div>
                <ModalSheetContentBlock expanded>
                  <TextAreaContainer
                    label="Reason"
                    model={requestScopeChangesVm}
                    field="reason"
                    name="reason"
                    data-test="request-scope-changes-reason-input"
                    help={`${requestScopeChangesVm.reasonCharsRemaining} characters remaining.`}
                    error={requestScopeChangesVm.submissionValidationStep.getError(
                      'reason'
                    )}
                    focusOnError
                  />
                </ModalSheetContentBlock>
              </ModalSheetContent>
            </div>

            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    requestScopeChangesVm.setState(
                      RequestScopeChangesState.UpdatingQuantity
                    )
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  loading={requestScopeChangesVm.request.pending}
                  onClick={requestScopeChangesVm.request}
                  dataTest="request-scope-changes-submit-button"
                >
                  Send Updates
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Success]: () => (
          <>
            <ModalSheetHeader withCloseButton />
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentIllustration
                  src={require('assets/images/img_welcome.svg')}
                />
                <ModalSheetContentTitle dataTest="request-scope-changes-success">
                  Thank you for your request
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  A Customer Success Representative will review and notify you
                  when updates have been approved.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={requestScopeChangesVm.close}>Close</Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Failed]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Unable to request scope changes
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {requestScopeChangesVm.error ? (
                    requestScopeChangesVm.error
                  ) : (
                    <p>
                      We were unable to request scope changes, it's possible
                      that the request currently is not in a state where changes
                      are available, or it could be a transient error. Please
                      try again. If the issue persists, please contact an
                      administrator.
                    </p>
                  )}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  disabled={disabled}
                  onClick={requestScopeChangesVm.close}
                >
                  Cancel
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
})

const FooterInventoryItemsQuantityInfo = observer(
  function FooterInventoryItemsQuantityInfo({
    totalSelectedInventoryItemsCount,
  }: {
    totalSelectedInventoryItemsCount: number
  }) {
    return (
      <div className={styles.footerInfo}>
        {totalSelectedInventoryItemsCount} selected
      </div>
    )
  }
)

const RequestScopeChangesDialogLeftSidePanelContent = observer(
  function RequestScopeChangesDialogLeftSidePanelContent({
    requestScopeChangesState,
  }: {
    requestScopeChangesState: RequestScopeChangesState
  }) {
    return (
      <LeftSidePanel>
        {match(requestScopeChangesState, {
          [RequestScopeChangesState.RequestingChanges]: () => (
            <>
              <LeftSidePanelHeading>1 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Select items to update</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Select all items that need to be either updated or removed
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.UpdatingQuantity]: () => (
            <>
              <LeftSidePanelHeading>2 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Update quantities</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Compare current item totals and capture new quantities
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.ReviewingChanges]: () => (
            <>
              <LeftSidePanelHeading>3 of 3</LeftSidePanelHeading>
              <LeftSidePanelTitle>Review changes</LeftSidePanelTitle>
              <LeftSidePanelDescription>
                Confirm updates and share why changes are being requested
              </LeftSidePanelDescription>
            </>
          ),
          [RequestScopeChangesState.Success]: () => undefined,
          [RequestScopeChangesState.Failed]: () => undefined,
        })}
      </LeftSidePanel>
    )
  }
)

const RequestScopeChangeHeading = observer(function RequestScopeChangeHeading({
  title,
  children,
}: {
  title: string
  children?: React.ReactNode
}) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.closeButtonContainer}>
            <ModalSheetHeaderCloseButton />
          </div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
})

const ItemHeadings = observer(function ItemHeadings({
  vm,
}: {
  vm: RequestScopeChangesViewModel
}) {
  return (
    <div className={styles.listItem}>
      <span>Service item</span>

      {vm.state === RequestScopeChangesState.UpdatingQuantity && (
        <span className={styles.listItemCurrent}>
          Current &nbsp;
          <Tooltip
            content={'Current quantity for each item on the job.'}
            placement="top"
          >
            <Icon name="info" size={18} color="var(--blue400)" />
          </Tooltip>
        </span>
      )}
      <span
        className={
          vm.state === RequestScopeChangesState.UpdatingQuantity
            ? styles.listItemNewTotalQuantitesState
            : styles.listItemNewTotal
        }
      >
        New total &nbsp;
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
    </div>
  )
})

const GreyContainer = observer(function GreyContainer({
  children,
  centerContent = false,
}: {
  children: React.ReactNode
  centerContent?: boolean
}) {
  return (
    <div
      className={cx(
        styles.greyBackround,
        centerContent && styles.centerContent
      )}
    >
      {children}
    </div>
  )
})
